/**
 * URL Validation Utilities
 * Validates and normalizes URLs for SEO analysis
 */

/**
 * Validate and normalize a URL
 * @param {string} inputUrl - URL to validate
 * @returns {Object} - { valid: boolean, cleanUrl: string, error: string | null }
 */
export const validateUrl = (inputUrl) => {
  if (!inputUrl || !inputUrl.trim()) {
    return { valid: false, error: "Please enter a URL" };
  }
  
  // Trim and clean the URL
  let cleanUrl = inputUrl.trim();
  
  // Check if URL has a protocol, if not add https://
  if (!cleanUrl.match(/^https?:\/\//i)) {
    cleanUrl = 'https://' + cleanUrl;
  }
  
  // Validate URL format using URL constructor
  try {
    const urlObj = new URL(cleanUrl);
    
    // Only allow http and https protocols
    if (!['http:', 'https:'].includes(urlObj.protocol)) {
      return { valid: false, error: "Only HTTP and HTTPS URLs are allowed" };
    }
    
    // Check for valid hostname (not empty, has at least one dot for domain)
    if (!urlObj.hostname || !urlObj.hostname.includes('.')) {
      return { valid: false, error: "Please enter a valid domain (e.g., example.com)" };
    }
    
    // Block localhost and private IPs for security
    const hostname = urlObj.hostname.toLowerCase();
    if (isBlockedHost(hostname)) {
      return { valid: false, error: "Local and private IP addresses are not allowed" };
    }
    
    return { valid: true, cleanUrl: cleanUrl, error: null, scheme: urlObj.protocol, hostname: hostname };
  } catch (e) {
    return { valid: false, error: "Please enter a valid URL (e.g., https://example.com)" };
  }
};

/**
 * Check if hostname is blocked (localhost or private IP)
 * @param {string} hostname - Hostname to check
 * @returns {boolean}
 */
export const isBlockedHost = (hostname) => {
  const blockedHosts = [
    'localhost',
    '127.0.0.1',
    '0.0.0.0'
  ];
  
  const privateRanges = [
    /^192\.168\./,
    /^10\./,
    /^172\.(1[6-9]|2[0-9]|3[0-1])\./
  ];
  
  if (blockedHosts.includes(hostname)) {
    return true;
  }
  
  for (const range of privateRanges) {
    if (range.test(hostname)) {
      return true;
    }
  }
  
  return false;
};

/**
 * Extract domain (hostname) from URL
 * @param {string} url - URL to parse
 * @returns {string} - Domain name
 */
export const extractDomain = (url) => {
  try {
    const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`);
    return urlObj.hostname;
  } catch {
    return url;
  }
};

/**
 * Get origin (scheme + hostname) for domain comparison. Returns null if invalid.
 * @param {string} inputUrl - URL to parse
 * @returns {string|null} - e.g. "https://example.com" or null
 */
export const getDomainOrigin = (inputUrl) => {
  const v = validateUrl(inputUrl);
  if (!v.valid || !v.scheme || !v.hostname) return null;
  return `${v.scheme}//${v.hostname}`;
};

/**
 * Validate domain-only input (no URL, protocol, or path).
 * @param {string} input - Domain to validate (e.g., example.com or blog.example.com)
 * @returns {Object} - { valid: boolean, domain?: string, error: string | null }
 */
export const validateDomain = (input) => {
  if (!input || !input.trim()) {
    return { valid: false, error: 'Please enter a domain' };
  }
  const domain = input.trim().toLowerCase();
  if (/^https?:\/\//i.test(domain) || domain.includes('://')) {
    return { valid: false, error: 'Enter a domain name only, without http:// or https://' };
  }
  if (domain.includes('/')) {
    return { valid: false, error: 'Enter a domain name only, without any path (e.g., example.com)' };
  }
  const domainRegex = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/;
  if (!domainRegex.test(domain)) {
    return { valid: false, error: 'Please enter a valid domain (e.g., example.com or blog.example.com)' };
  }
  if (isBlockedHost(domain)) {
    return { valid: false, error: 'Local and private IP addresses are not allowed' };
  }
  return { valid: true, domain, error: null };
};

export default {
  validateUrl,
  isBlockedHost,
  extractDomain,
  getDomainOrigin,
  validateDomain
};
